import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { schoolService } from '../../../services/schoolService';

const SchoolDetailsStep = ({ formData, errors, onChange }) => {
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);

  useEffect(() => {
    loadSchools();
  }, []);

  const loadSchools = async () => {
    try {
      setLoadingSchools(true);
      const schoolData = await schoolService?.getAllSchools(null);
      setSchools(schoolData || []);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      setLoadingSchools(false);
    }
  };

  const ageCategoryOptions = [
    { 
      value: 'boys_u14', 
      label: 'Boys Under 14',
      description: 'Born between 01/09/2012 and 31/08/2015'
    },
    { 
      value: 'girls_u15', 
      label: 'Girls Under 15',
      description: 'Born between 01/09/2011 and 31/08/2014'
    },
    { 
      value: 'boys_u16', 
      label: 'Boys Under 16',
      description: 'Born between 01/09/2010 and 31/08/2012'
    }
  ];

  // Convert database schools to Select options
  const schoolOptions = schools?.map(school => ({
    value: school?.id, // Use database ID as value
    label: school?.name,
    email: school?.email
  }));

  // Handle school selection - update both schoolId and schoolName
  const handleSchoolChange = (selectedSchoolId) => {
    const selectedSchool = schools?.find(s => s?.id === selectedSchoolId);
    
    // Update both schoolId and schoolName in formData
    onChange('schoolId', selectedSchoolId);
    onChange('schoolName', selectedSchool?.name || '');
    
    // Auto-fill school email if available
    if (selectedSchool?.email && !formData?.schoolEmail) {
      onChange('schoolEmail', selectedSchool?.email);
    }
    
    // Auto-fill city from selected school
    if (selectedSchool?.city) {
      onChange('city', selectedSchool?.city);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-primary font-semibold">1</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">School Details</h3>
            <p className="text-sm text-muted-foreground">
              Select your age category and provide school information
            </p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 space-y-6">
        <Select
          label="Age Category"
          description="Select the age group for your team. Cutoff dates are strictly enforced."
          placeholder="Choose age category"
          options={ageCategoryOptions}
          value={formData?.ageCategory}
          onChange={(value) => onChange('ageCategory', value)}
          error={errors?.ageCategory}
          required
        />

        <Select
          label="School Name"
          description="Search and select your school from the list"
          placeholder="Search for your school"
          options={schoolOptions}
          value={formData?.schoolId} // Use schoolId for value
          onChange={handleSchoolChange} // Custom handler
          error={errors?.schoolName}
          searchable
          loading={loadingSchools}
          required
        />

        <Input
          label="School Email Address"
          type="email"
          placeholder="school@example.com"
          description="Official school email for communication"
          value={formData?.schoolEmail}
          onChange={(e) => onChange('schoolEmail', e?.target?.value)}
          error={errors?.schoolEmail}
          required
        />

        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="text-warning flex-shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-warning-foreground mb-1">Important Information</p>
              <p className="text-xs text-muted-foreground">
                Ensure all details are accurate. Age category selection cannot be changed after submission. Player eligibility will be verified based on the cutoff dates mentioned.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolDetailsStep;